import uniqBy from 'lodash-es/uniqBy'
import { Person } from '@/api/entities/people/types'
import { SubtaskStatus } from '@/api/pathways/subtasks/types'
import { useCommunicationsStore } from '@/legacy/store/modules/communications'
import { useConfigStore } from '@/legacy/store/modules/config'
import { useNotificationStore } from '@/legacy/store/modules/notification'
import {
  isEvergreenPathway,
  usePathwayApi,
} from '@/legacy/store/modules/pathways'
import { usePeopleApi } from '@/legacy/store/modules/patient'
import {
  useMapEntityOtherContactPhoneNumberApi,
  useMapEntityPhoneNumberApi,
} from '@/legacy/store/modules/phoneNumbers'
import { useTaskApi } from '@/legacy/store/modules/tasks'
// nosemgrep: no-v2-imports
import {
  SMS_PARTS,
  useTextingSubtaskByIdApi,
  useSmsCommByPhoneNumberIdApi,
} from '@/legacy/store/modules/textingV2'
import { IdMap } from '@/legacy/types/api/store'
import {
  Communication,
  CommunicationType,
} from '@/legacy/types/communications/communications'
import {
  CURRENT_MEMBER_STR,
  PhoneNumberNameAndType,
} from '@/legacy/types/entities/phoneNumbers'
import { NotificationType } from '@/legacy/types/notifications'
import { Pathway } from '@/legacy/types/pathways/pathways'
import { SubtaskPayload } from '@/legacy/types/pathways/subtasks'
import { Contact } from '@/legacy/types/patients/contacts'
import { NormalizedPatient } from '@/legacy/types/patients/patients'
import { formatNameFromPerson } from '@/libs/format/format'
import { formatPhone } from '@/libs/phoneNumber'
import { SMS_COMM_BY_PHONE_NUMBER_ID_SORT_BY } from '@/pages/PatientProfile/Tabs/TextingTab/TextingView/shared/types'
import {
  contactPersonObj,
  displayRelationship,
  getContactFromStore,
} from './contacts'

/**
 *
 * @param phoneNumberString
 * @param otherContact
 * @param entityIds
 */
export async function getPhoneNumberEntityMaps(
  phoneNumberString: string | null,
  otherContact = false,
  entityIds?: string[]
) {
  if (otherContact) {
    const res = await useMapEntityOtherContactPhoneNumberApi().list({
      params: {
        ...(phoneNumberString?.length
          ? { filter_free_text_phone_number: phoneNumberString }
          : {}),
        ...(entityIds?.length ? { filter_entity_ids: entityIds } : {}),
      },
    })
    return res.data
  }
  const res = await useMapEntityPhoneNumberApi().list({
    params: {
      ...(phoneNumberString?.length
        ? { filter_free_text_phone_number: phoneNumberString }
        : {}),
      ...(entityIds?.length ? { filter_entity_ids: entityIds } : {}),
    },
  })
  return res.data
}

/**
 *
 * @param isCurrentMember
 * @param person
 * @param isContact
 * @param currentEntityId
 * @param contacts
 */
async function setUpNameAndTypeDetailsForMappings(
  isCurrentMember: boolean,
  person: Person | null,
  isContact: boolean,
  currentEntityId: string,
  contacts: IdMap<Contact> | null | undefined
) {
  let name: string
  let type: string

  if (isCurrentMember && person) {
    name = formatNameFromPerson(person)
    type = CURRENT_MEMBER_STR
  } else if (isContact) {
    const contact = getContactFromStore(currentEntityId, contacts ?? {})
    const contactPerson = contact ? contactPersonObj(contact) : undefined
    name = contactPerson
      ? formatNameFromPerson(contactPerson)
      : 'Unknown Contact'

    if (contact) {
      type = displayRelationship(contact.relationshipToPatient) ?? 'N/A'
    } else {
      type = 'N/A'
    }
  } else {
    const personDetails = await usePeopleApi().retrieve({
      ids: [currentEntityId],
    })
    name = personDetails
      ? formatNameFromPerson(personDetails)
      : 'Unknown Other Member or Contact'
    type = "Other TC member or other TC member's contact"
  }

  return { name, type }
}

/**
 * Function to get and set contact phone number maps
 * e.g. { 'phone_number_string': <entity_ids>[]  }
 * @param phoneNumberStrings
 * @param contactEntityIds
 * @param person
 * @param contacts
 * @param patient
 * @param otherContact
 */
export async function mapPhoneNumberToNames(
  phoneNumberStrings: string[],
  contactEntityIds: string[],
  person: Person | null,
  contacts: IdMap<Contact> | null | undefined,
  patient: NormalizedPatient | null,
  otherContact = false
) {
  const phoneNumberMaps: {
    [key: string]: { name: string; type: string }[]
  } = {}
  for (const phoneNumber of phoneNumberStrings) {
    const retrievedMaps = await getPhoneNumberEntityMaps(
      phoneNumber,
      otherContact
    )
    if (retrievedMaps.length) {
      for (const phoneNumberMap of retrievedMaps) {
        const currentEntityId = phoneNumberMap.entityId
        const phoneNumberStr = phoneNumberMap.phoneNumber.phoneNumber
        const isCurrentMember = currentEntityId === patient?.entityId
        const isContact = contactEntityIds.includes(currentEntityId)

        const mappingDetails = await setUpNameAndTypeDetailsForMappings(
          isCurrentMember,
          person,
          isContact,
          currentEntityId,
          contacts
        )

        if (phoneNumberMaps[phoneNumberStr]) {
          phoneNumberMaps[phoneNumberStr].push(mappingDetails)
          phoneNumberMaps[phoneNumberStr] = uniqBy(
            phoneNumberMaps[phoneNumberStr],
            'name'
          )
        } else {
          phoneNumberMaps[phoneNumberStr] = [mappingDetails]
        }
      }
    }
  }
  return phoneNumberMaps
}

/**
 *
 * @param namesAndTypes
 */
export function renderSharedNumberWarningMessageForContacts(
  namesAndTypes: { name: string; type: string }[]
) {
  let str = 'Shared phone number between '
  for (const [idx, { name, type }] of namesAndTypes.entries()) {
    if (idx !== 0) {
      str += ` and ${name} (${type})`
    } else {
      str += `${name} (${type})`
    }
  }

  return str
}

/**
 *
 * @param namesAndTypes
 * @param phoneNumberString
 */
export function renderSharedNumberWarningMessageForPatient(
  namesAndTypes: PhoneNumberNameAndType[],
  phoneNumberString: string | number
) {
  const otherNamesAndTypes = namesAndTypes.filter(
    ({ type }) => type !== CURRENT_MEMBER_STR
  )
  if (!otherNamesAndTypes.length) return

  let str = `Shared number between member (${formatPhone(phoneNumberString)}) `
  for (const [idx, { name, type }] of otherNamesAndTypes.entries()) {
    if (idx !== 0) {
      str += ` and ${name} (${type})`
    } else {
      str += `${name} (${type})`
    }
  }

  return str
}

/**
 *
 * @param subtaskId
 * @param payload
 */
export async function updatePointerSubtask(
  subtaskId: string,
  payload: Partial<SubtaskPayload>
) {
  try {
    await useTextingSubtaskByIdApi().partialUpdate({
      ids: [subtaskId],
      body: payload,
    })
  } catch (err) {
    console.error(err)
    useNotificationStore().setNotification({
      message: 'Failed to update texting subtask.',
      type: NotificationType.DANGER,
    })
    return
  }
}

/**
 *
 * @param taskId
 * @param pointerToCommId
 * @param pathwayId
 */
export async function createPointerSubtaskForComm(
  taskId: string,
  pointerToCommId: string,
  pathwayId: string
) {
  // grab taskVariantId to create new texting subtask for current patient
  const tasks = await useTaskApi().list({
    params: {
      filter_task_ids: [taskId],
    },
  })

  try {
    // create new texting task
    const createdTask = await useTaskApi().create({
      body: {
        taskVariantId: tasks.data[0].taskVariantId,
        pathwayId: pathwayId,
      },
    })

    // update the created subtask to have a commId
    if (!createdTask.subtasks?.length) {
      throw new Error('No subtasks created for texting task')
    }
    await updatePointerSubtask(createdTask.subtasks[0].subtaskId, {
      pointerToCommId: pointerToCommId,
    })
  } catch (err) {
    console.error(err)
    useNotificationStore().setNotification({
      message: 'Failed to create texting subtask.',
      type: NotificationType.DANGER,
    })
    return
  }
}

/**
 *
 * @param comm
 * @param evergreenPathway
 */
export async function closeOldSubtaskAndCreateNewOne(
  comm: Communication,
  evergreenPathway: Pathway
) {
  const subtasks = await useTextingSubtaskByIdApi().list({
    params: {
      filter_pointer_comm_ids: [comm.communicationId],
    },
  })
  const pointerSubtask = subtasks.data
  if (pointerSubtask.length) {
    // close previous pointer subtask
    await updatePointerSubtask(pointerSubtask[0].subtaskId, {
      status: SubtaskStatus.COMPLETED,
    })
    // create new pointer subtask for current patient
    await createPointerSubtaskForComm(
      pointerSubtask[0].taskIds[0],
      pointerSubtask[0].pointerToCommId as string,
      evergreenPathway.pathwayId
    )
  }
  return
}

/**
 * Reads the unknown-texts patient ID from config. Notifies and returns
 * undefined if not configured.
 */
function _getUnknownTextsPatientId(): string | undefined {
  const id = useConfigStore().configVals?.textingSubtasksUnknownNumberPatientId
  if (!id) {
    useNotificationStore().setNotification({
      message: 'Unknown texts patient not configured.',
      type: NotificationType.DANGER,
      error: new Error('Unknown texts patient not configured.'),
    })
  }
  return id
}

/**
 * Finds the evergreen pathway from the pathway store. Notifies and returns
 * undefined if not found.
 */
function _findEvergreenPathway(): Pathway | undefined {
  const pathway = Object.values(usePathwayApi().data ?? {}).find((p) =>
    isEvergreenPathway(p.title)
  )
  if (!pathway) {
    useNotificationStore().setNotification({
      message: 'Evergreen pathway not found.',
      type: NotificationType.DANGER,
      error: new Error('Evergreen pathway not found.'),
    })
  }
  return pathway
}

/**
 * Reassigns a list of communications to the given patient in parallel.
 * @param comms
 * @param patientEntityId
 */
async function _reassignCommsToPatient(
  comms: Communication[],
  patientEntityId: string
): Promise<void> {
  const promises: Promise<Communication>[] = []
  for (const comm of comms) {
    promises.push(
      useCommunicationsStore().updateCommunication(comm.communicationId, {
        patientIds: [patientEntityId],
      })
    )
  }
  await Promise.all(promises)
}

/**
 * Fetches the 10 most recent SMS communications for a phone number that are
 * assigned to the unknown-texts patient.
 * @param phoneNumberId
 * @param unknownPatientId
 */
async function _fetchRecentUnknownTextsComms(
  phoneNumberId: string,
  unknownPatientId: string
): Promise<Communication[]> {
  const result = await useSmsCommByPhoneNumberIdApi().list({
    params: {
      filter_phone_number_ids: [phoneNumberId],
      filter_types: CommunicationType.Text,
      sort_by: SMS_COMM_BY_PHONE_NUMBER_ID_SORT_BY,
      page_length: 10,
      page_number: 1,
      parts: SMS_PARTS,
      filter_patient_ids: [unknownPatientId],
    },
  })
  return result.data
}

/**
 * Migrates text communications from the unknown texts patient to a real patient.
 * Called when a phone number previously mapped to the unknown texts patient is
 * added to a real patient's profile or one of their contacts.
 * @param phoneNumberId
 * @param patientEntityId
 */
export async function migrateUnknownTextsToPatient(
  phoneNumberId: string,
  patientEntityId: string
) {
  const TEXTING_UNKNOWN_NUMBERS_PATIENT_ID = _getUnknownTextsPatientId()
  if (!TEXTING_UNKNOWN_NUMBERS_PATIENT_ID) return

  const evergreenPathway = _findEvergreenPathway()
  if (!evergreenPathway) return

  const mostRecentComms = await _fetchRecentUnknownTextsComms(
    phoneNumberId,
    TEXTING_UNKNOWN_NUMBERS_PATIENT_ID
  )
  if (!mostRecentComms.length) return

  await Promise.all([
    closeOldSubtaskAndCreateNewOne(mostRecentComms[0], evergreenPathway),
    _reassignCommsToPatient(mostRecentComms, patientEntityId),
  ])
}
